import React, { useState } from "react";

export default function TrackPicker({
  tracks,
  currentIndex,
  onPick,
  volume,
  onVolumeChange,
  surround,
  onToggleSurround,
  directionHint = "look right to hear it",
}) {
  // Open by default on >= 1200px screens, closed by default on smaller screens (< 1200px)
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 1200;
  });

  const currentTrack = tracks[currentIndex] || tracks[0];

  // 1. Collapsed Floating Pill Chip
  if (!isOpen) {
    return (
      <div
        data-testid="track-picker-chip"
        className="hud-in"
        style={{
          position: "fixed",
          top: "clamp(16px, 2.5vh, 22px)",
          right: "clamp(16px, 2.5vw, 22px)",
          zIndex: 7,
          pointerEvents: "auto",
        }}
      >
        <button
          data-testid="track-picker-toggle-btn"
          onClick={() => setIsOpen(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 14px",
            background: "rgba(10, 10, 15, 0.88)",
            color: "var(--paper)",
            border: "1px solid rgba(0, 211, 217, 0.4)",
            borderRadius: 999,
            boxShadow: "0 6px 20px rgba(0,0,0,0.35), 0 0 14px rgba(0,211,217,0.15)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.08em",
            cursor: "pointer",
            backdropFilter: "blur(8px)",
          }}
          title="Open Track & Audio Controls"
        >
          <span
            style={{
              display: "inline-block",
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "var(--neon-magenta)",
              boxShadow: "0 0 8px var(--neon-magenta)",
              flexShrink: 0,
            }}
          />
          <span style={{ fontWeight: 700, color: "var(--neon-cyan)" }}>
            ♫ {currentTrack.title}
          </span>
          {surround && (
            <span
              style={{
                fontSize: 8,
                padding: "1px 5px",
                borderRadius: 4,
                background: "rgba(255,46,136,0.25)",
                color: "var(--neon-magenta)",
                border: "1px solid var(--neon-magenta)",
                fontWeight: 700,
              }}
            >
              SURROUND
            </span>
          )}
          <span style={{ opacity: 0.6, fontSize: 9 }}>▼</span>
        </button>
      </div>
    );
  }

  // 2. Expanded Glassmorphic Control Panel
  return (
    <div
      data-testid="track-picker"
      className="hud-in"
      style={{
        position: "fixed",
        top: "clamp(16px, 2.5vh, 22px)",
        right: "clamp(16px, 2.5vw, 22px)",
        zIndex: 7,
        background: "rgba(10,10,15,0.88)",
        color: "var(--paper)",
        padding: "14px 16px",
        borderRadius: 12,
        border: "1px solid rgba(0,211,217,0.35)",
        boxShadow: "0 8px 30px rgba(0,0,0,0.35), 0 0 20px rgba(0,211,217,0.12)",
        fontFamily: "var(--font-mono)",
        minWidth: 240,
        maxWidth: 280,
        maxHeight: "calc(100vh - 80px)",
        overflowY: "auto",
        pointerEvents: "auto",
        backdropFilter: "blur(10px)",
      }}
    >
      {/* Header with Minimize Toggle */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--neon-cyan)" }}>
          <span style={{ width: 6, height: 6, background: "var(--neon-magenta)", display: "inline-block", boxShadow: "0 0 8px var(--neon-magenta)" }} />
          vinyl · side b
        </div>
        <button
          data-testid="track-picker-close-btn"
          onClick={() => setIsOpen(false)}
          style={{
            background: "transparent",
            border: "none",
            color: "rgba(245,245,242,0.6)",
            fontSize: 13,
            cursor: "pointer",
            padding: "2px 6px",
            lineHeight: 1,
            borderRadius: 4,
          }}
          title="Minimize to chip"
        >
          ▲
        </button>
      </div>

      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        {tracks.map((t, i) => {
          const active = i === currentIndex;
          return (
            <li key={t.id}>
              <button
                data-testid={`track-${t.id}`}
                onClick={() => onPick(i)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 10px",
                  background: active ? "rgba(255,46,136,0.15)" : "transparent",
                  color: active ? "var(--paper)" : "rgba(245,245,242,0.75)",
                  border: active ? "1px solid rgba(255,46,136,0.55)" : "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 8,
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  letterSpacing: "0.05em",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 8, height: 8, borderRadius: "50%",
                    background: active ? "var(--neon-magenta)" : "rgba(245,245,242,0.35)",
                    boxShadow: active ? "0 0 8px var(--neon-magenta)" : "none",
                    flexShrink: 0,
                  }}
                />
                <span style={{ flex: 1, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    <span style={{ fontWeight: 700 }}>{t.title}</span>
                    {t.artist && <span style={{ opacity: 0.5, marginLeft: 8, fontSize: 10 }}>{t.artist}</span>}
                  </span>
                  {t.badge && (
                    <span
                      style={{
                        fontSize: 8,
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: active ? "var(--neon-magenta)" : "rgba(0, 211, 217, 0.15)",
                        color: active ? "var(--paper)" : "var(--neon-cyan)",
                        border: `1px solid ${active ? "var(--neon-magenta)" : "rgba(0, 211, 217, 0.4)"}`,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {t.badge}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Volume slider */}
      <div style={{ marginTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(245,245,242,0.6)", marginBottom: 8 }}>
          <span>volume</span>
          <span data-testid="volume-value" style={{ color: "var(--neon-cyan)", fontWeight: 700 }}>{Math.round((volume ?? 0) * 100)}%</span>
        </div>
        <input
          type="range"
          data-testid="volume-slider"
          min={0}
          max={1}
          step={0.01}
          value={volume ?? 0}
          onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
          style={{
            width: "100%",
            accentColor: "var(--neon-magenta)",
            cursor: "pointer",
          }}
        />
      </div>

      {/* Surround Sound toggle */}
      <div style={{ marginTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: surround ? "var(--neon-magenta)" : "rgba(245,245,242,0.85)", fontWeight: 700 }}>
              <span>surround sound</span>
              {surround && (
                <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 4, background: "rgba(255,46,136,0.25)", border: "1px solid var(--neon-magenta)", color: "var(--neon-magenta)" }}>
                  ON
                </span>
              )}
            </div>
            <div style={{ fontSize: 9, color: "rgba(245,245,242,0.45)", letterSpacing: "0.08em", marginTop: 2 }}>
              {surround ? "all directions · omni" : "3d spatial · east wall"}
            </div>
          </div>
          <button
            data-testid="surround-toggle-btn"
            onClick={() => onToggleSurround && onToggleSurround(!surround)}
            aria-label="Toggle Surround Sound"
            style={{
              width: 42,
              height: 22,
              borderRadius: 11,
              background: surround ? "var(--neon-magenta)" : "rgba(255,255,255,0.12)",
              border: surround ? "1px solid var(--neon-magenta)" : "1px solid rgba(255,255,255,0.2)",
              position: "relative",
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: surround ? "0 0 12px rgba(255,46,136,0.6)" : "none",
              padding: 0,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                position: "absolute",
                top: 2,
                left: surround ? 22 : 2,
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: "var(--paper)",
                transition: "left 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
              }}
            />
          </button>
        </div>
      </div>

      <div
        data-testid="audio-direction-hint"
        style={{
          marginTop: 12,
          fontSize: 9,
          color: surround ? "var(--neon-cyan)" : (directionHint === "facing record player" ? "var(--neon-magenta)" : "rgba(245,245,242,0.55)"),
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          display: "flex",
          alignItems: "center",
          gap: 6,
          transition: "color 0.2s ease",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: surround ? "var(--neon-cyan)" : (directionHint === "facing record player" ? "var(--neon-magenta)" : "rgba(245,245,242,0.4)"),
            boxShadow: surround ? "0 0 6px var(--neon-cyan)" : (directionHint === "facing record player" ? "0 0 6px var(--neon-magenta)" : "none"),
            flexShrink: 0,
            transition: "all 0.2s ease",
          }}
        />
        <span>{surround ? "all-around immersion" : directionHint}</span>
      </div>
    </div>
  );
}
